import React, { useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import i18n from "i18next";
import { Alert, Button, Checkbox, Form, Input, Modal, Space, Spin, Typography } from "antd";
import { ServiceRepository } from "../../redux/ServiceActions";
import settings from "../../virtuoso/settings";

const { Text } = Typography;

const EMPTY_COUNTS = { subject: 0, predicate: 0, object: 0 };
const EMPTY_SELECTION = { subject: false, predicate: false, object: false };

const bindingValue = (binding, name) => {
  const value = binding?.[name];
  return typeof value === "object" ? value?.value : value;
};

const isUri = (value) => {
  try {
    return !!value && !!new URL(value);
  } catch {
    return false;
  }
};

/**
 * Базовый диалог замены URI.
 *
 * sourceUri/targetUri делают поля управляемыми внешним компонентом.
 * sourceUriReadOnly позволяет выбирать исходный URI во внешнем диалоге,
 * targetUriReadOnly — передать фиксированный целевой URI без возможности изменения.
 */
export function UriReplacement({
  open,
  onClose,
  onReplaced,
  dispatch,
  graph = settings.catalog.graph.iri,
  sourceUri,
  targetUri,
  initialSourceUri = "",
  initialTargetUri = "",
  onSourceUriChange,
  onTargetUriChange,
  sourceUriReadOnly = false,
  targetUriReadOnly = false,
  sourceUriExtra = null,
  title = i18n.t("service.uriReplacement.title"),
}) {
  const [localSourceUri, setLocalSourceUri] = useState(initialSourceUri);
  const [localTargetUri, setLocalTargetUri] = useState(initialTargetUri);
  const [counts, setCounts] = useState(EMPTY_COUNTS);
  const [selected, setSelected] = useState(EMPTY_SELECTION);
  const [checking, setChecking] = useState(false);
  const [replacing, setReplacing] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const checkSequence = useRef(0);

  const oldUri = sourceUri !== undefined ? sourceUri : localSourceUri;
  const newUri = targetUri !== undefined ? targetUri : localTargetUri;
  const normalizedOldUri = String(oldUri || "").trim();
  const normalizedNewUri = String(newUri || "").trim();

  useEffect(() => {
    if (!open) return;
    if (sourceUri === undefined) setLocalSourceUri(initialSourceUri);
    if (targetUri === undefined) setLocalTargetUri(initialTargetUri);
    setCounts(EMPTY_COUNTS);
    setSelected(EMPTY_SELECTION);
    setError(null);
    setSuccess(false);
  }, [open, sourceUri, targetUri, initialSourceUri, initialTargetUri]);

  useEffect(() => {
    if (!open) return undefined;

    const sequence = ++checkSequence.current;
    setCounts(EMPTY_COUNTS);
    setSelected(EMPTY_SELECTION);
    setError(null);
    setSuccess(false);

    if (!normalizedOldUri) {
      setChecking(false);
      return undefined;
    }
    if (!isUri(normalizedOldUri)) {
      setChecking(false);
      setError(i18n.t("service.uriReplacement.invalidSourceUri"));
      return undefined;
    }

    setChecking(true);
    const timer = setTimeout(() => {
      dispatch(ServiceRepository.usedCountIRI(graph, normalizedOldUri))
        .then((response) => {
          if (sequence !== checkSequence.current) return;
          const bindings = response?.results?.bindings || response?.bindings || [];
          const nextCounts = bindings.reduce((result, binding) => {
            const position = bindingValue(binding, "position");
            if (Object.prototype.hasOwnProperty.call(result, position)) {
              result[position] = Number(bindingValue(binding, "count")) || 0;
            }
            return result;
          }, { ...EMPTY_COUNTS });

          setCounts(nextCounts);
          setSelected({
            subject: false,
            predicate: nextCounts.predicate > 0,
            object: nextCounts.object > 0,
          });
        })
        .catch((reason) => {
          if (sequence === checkSequence.current) {
            setError(reason?.message || String(reason));
          }
        })
        .finally(() => {
          if (sequence === checkSequence.current) setChecking(false);
        });
    }, 350);

    return () => clearTimeout(timer);
  }, [open, graph, normalizedOldUri, dispatch]);

  const available = useMemo(() => ({
    subject: counts.subject > 0,
    predicate: counts.predicate > 0,
    object: counts.object > 0,
  }), [counts]);
  const hasAvailable = available.subject || available.predicate || available.object;
  const hasSelected = selected.subject || selected.predicate || selected.object;
  const targetError = !!normalizedNewUri && !isUri(normalizedNewUri);
  const sameUri = !!normalizedOldUri && normalizedOldUri === normalizedNewUri;
  const canReplace = hasSelected && isUri(normalizedOldUri) && isUri(normalizedNewUri)
    && !sameUri && !checking && !replacing && !success;

  const changeSourceUri = (event) => {
    const value = event.target.value;
    if (sourceUri === undefined) setLocalSourceUri(value);
    onSourceUriChange?.(value);
  };

  const changeTargetUri = (event) => {
    const value = event.target.value;
    if (targetUri === undefined) setLocalTargetUri(value);
    onTargetUriChange?.(value);
  };

  const handleReplace = async () => {
    if (!canReplace) return;
    setReplacing(true);
    setError(null);
    setSuccess(false);

    try {
      if (selected.subject) {
        await dispatch(ServiceRepository.replaceSubjectIRI(graph, normalizedOldUri, normalizedNewUri));
      }
      if (selected.predicate) {
        await dispatch(ServiceRepository.replacePredicateIRI(graph, normalizedOldUri, normalizedNewUri));
      }
      if (selected.object) {
        await dispatch(ServiceRepository.replaceObjectIRI(graph, normalizedOldUri, normalizedNewUri));
      }
      setSuccess(true);
      onReplaced?.({
        sourceUri: normalizedOldUri,
        targetUri: normalizedNewUri,
        positions: Object.keys(selected).filter((position) => selected[position]),
      });
    } catch (reason) {
      setError(reason?.message || String(reason));
    } finally {
      setReplacing(false);
    }
  };

  return (
    <Modal
      title={title}
      open={open}
      onCancel={onClose}
      maskClosable={!checking && !replacing}
      closable={!replacing}
      footer={[
        <Button key="cancel" onClick={onClose} disabled={replacing}>{i18n.t("service.uriReplacement.cancel")}</Button>,
        <Button key="replace" type="primary" danger onClick={handleReplace}
          disabled={!canReplace} loading={replacing}>{i18n.t("service.uriReplacement.replace")}</Button>,
      ]}
    >
      <Form layout="vertical">
        <Form.Item label={i18n.t("service.uriReplacement.sourceUri")} validateStatus={normalizedOldUri && !isUri(normalizedOldUri) ? "error" : ""}>
          <Input value={oldUri} onChange={changeSourceUri} readOnly={sourceUriReadOnly}
            disabled={replacing || success} placeholder="https://example.org/old-uri" />
        </Form.Item>
        {sourceUriExtra}
        <Form.Item label={i18n.t("service.uriReplacement.targetUri")}
          validateStatus={targetError || sameUri ? "error" : ""}
          help={targetError ? i18n.t("service.uriReplacement.invalidTargetUri")
            : sameUri ? i18n.t("service.uriReplacement.urisMustDiffer") : null}>
          <Input value={newUri} onChange={changeTargetUri} readOnly={targetUriReadOnly}
            disabled={replacing || success} placeholder="https://example.org/new-uri" />
        </Form.Item>

        <Space direction="vertical" size={8} style={{ width: "100%" }}>
          <Text strong>{i18n.t("service.uriReplacement.positions")}</Text>
          <Checkbox checked={selected.subject} disabled={!available.subject || checking || replacing || success}
            onChange={(event) => setSelected((value) => ({ ...value, subject: event.target.checked }))}>
            {i18n.t("service.uriReplacement.subjects", { count: counts.subject })}
          </Checkbox>
          <Checkbox checked={selected.predicate} disabled={!available.predicate || checking || replacing || success}
            onChange={(event) => setSelected((value) => ({ ...value, predicate: event.target.checked }))}>
            {i18n.t("service.uriReplacement.predicates", { count: counts.predicate })}
          </Checkbox>
          <Checkbox checked={selected.object} disabled={!available.object || checking || replacing || success}
            onChange={(event) => setSelected((value) => ({ ...value, object: event.target.checked }))}>
            {i18n.t("service.uriReplacement.objects", { count: counts.object })}
          </Checkbox>
        </Space>
      </Form>

      {checking && <div style={{ marginTop: 16 }}><Spin size="small" /> <Text>{i18n.t("service.uriReplacement.checkingUsage")}</Text></div>}
      {hasAvailable && !success && <Alert style={{ marginTop: 16 }} type="warning" showIcon
        message={i18n.t("service.uriReplacement.backupWarning")} />}
      {error && <Alert style={{ marginTop: 16 }} type="error" showIcon message={i18n.t("service.uriReplacement.error")} description={error} />}
      {success && <Alert style={{ marginTop: 16 }} type="success" showIcon message={i18n.t("service.uriReplacement.success")} />}
    </Modal>
  );
}

export default connect()(UriReplacement);
